import {
  useState,
  useEffect,
  useRef,
  lazy,
  Suspense,
  startTransition,
} from "react";
import { takeDestination } from "@/shared/lib/pendingDestination";
import type { ReactNode } from "react";
import { LanguageProvider } from "@/shared/i18n/LanguageContext";
import { AuthProvider, useAuth } from "@/shared/contexts/AuthContext";
import {
  SubscriptionProvider,
  useSubscription,
} from "@/shared/contexts/SubscriptionContext";
import {
  VerticalProvider,
  useVertical,
} from "@/shared/contexts/VerticalContext";
import { ProductProvider } from "@/shared/contexts/ProductContext";
import { productIntentFromHost } from "@/shared/lib/productIntent";
import { ProductDemoProvider } from "@/shared/contexts/ProductDemoContext";
import type { AcademicProgramSlug } from "@/shared/pages/solutions/AcademicProgramPage";
import { DashboardPage } from "@/shared/pages/DashboardPage";
import { LoginPage } from "@/shared/pages/LoginPage";
import { SignupPage } from "@/shared/pages/SignupPage";
// Static (not lazy) on purpose: a lazy ResetPasswordPage chunk emits CSS
// byte-identical to ForgotPasswordPage's chunk, and same-content chunks
// collide on their content-hashed output path under Bun 1.3.0 (deploy build).
import { ResetPasswordPage } from "@/shared/pages/ResetPasswordPage";
import { HomePage } from "@/shared/pages/HomePage";
import { isGeneralDemoPath, isGeneralDemo, generalDemoDestination } from "@/shared/demo/generalDemoRoutes";
const CourseWizardDemoPage = lazy(() => import("@/shared/pages/CourseWizardDemoPage").then(module => ({ default: module.CourseWizardDemoPage })));
const GeneralWorkspaceDemoPage = lazy(() => import("@/shared/pages/GeneralWorkspaceDemoPage").then(module => ({ default: module.GeneralWorkspaceDemoPage })));

// TEMP DEV: walkie/phone treatment tuner. Reach it at /walkie-test. Delete with the route case + file.
const WalkieTestPage = lazy(() => import("@/shared/pages/WalkieTestPage"));
const SessionsPage = lazy(() =>
  import("@/shared/pages/SessionsPage").then((m) => ({
    default: m.SessionsPage,
  })),
);
const ConversationMapPage = lazy(() =>
  import("@/shared/pages/ConversationMapPage").then((m) => ({
    default: m.ConversationMapPage,
  })),
);
const ProfilePage = lazy(() =>
  import("@/shared/pages/ProfilePage").then((m) => ({
    default: m.ProfilePage,
  })),
);
const CoursePage = lazy(() =>
  import("@/shared/pages/CoursePage").then((m) => ({ default: m.CoursePage })),
);
const CourseProgramPage = lazy(() =>
  import("@/shared/pages/CourseProgramPage").then((m) => ({
    default: m.CourseProgramPage,
  })),
);
const CourseWeekPage = lazy(() =>
  import("@/shared/pages/CourseWeekPage").then((m) => ({
    default: m.CourseWeekPage,
  })),
);
const LessonPage = lazy(() =>
  import("@/shared/pages/LessonPage").then((m) => ({ default: m.LessonPage })),
);
const CourseLiveSessionPage = lazy(() =>
  import("@/shared/pages/CourseLiveSessionPage").then((m) => ({
    default: m.CourseLiveSessionPage,
  })),
);
const CoursesPage = lazy(() =>
  import("@/shared/pages/CoursesPage").then((m) => ({
    default: m.CoursesPage,
  })),
);
const ExpertPage = lazy(() =>
  import("@/shared/pages/ExpertPage").then((m) => ({ default: m.ExpertPage })),
);
const ProgramPage = lazy(() =>
  import("@/shared/pages/ProgramPage").then((m) => ({
    default: m.ProgramPage,
  })),
);
const CertificateViewPage = lazy(() =>
  import("@/shared/pages/CertificateViewPage").then((m) => ({
    default: m.CertificateViewPage,
  })),
);
const TermsPage = lazy(() =>
  import("@/shared/pages/TermsPage").then((m) => ({ default: m.TermsPage })),
);
const PrivacyPage = lazy(() =>
  import("@/shared/pages/PrivacyPage").then((m) => ({
    default: m.PrivacyPage,
  })),
);
const RefundPolicyPage = lazy(() =>
  import("@/shared/pages/RefundPolicyPage").then((m) => ({
    default: m.RefundPolicyPage,
  })),
);
const ContactPage = lazy(() =>
  import("@/shared/pages/ContactPage").then((m) => ({
    default: m.ContactPage,
  })),
);
const CrisisCasesPage = lazy(() =>
  import("@/products/crisis-operations-workspace/CrisisCasesPage").then(
    (m) => ({ default: m.CrisisCasesPage }),
  ),
);
const CrisisCaseDetailPage = lazy(() =>
  import("@/products/crisis-operations-workspace/CrisisCaseDetailPage").then(
    (m) => ({ default: m.CrisisCaseDetailPage }),
  ),
);
const CrisisRecordedSessionPage = lazy(() =>
  import("@/products/crisis-operations-workspace/CrisisRecordedSessionPage").then(
    (m) => ({ default: m.CrisisRecordedSessionPage }),
  ),
);
const SupervisorPage = lazy(() =>
  import("@/shared/pages/SupervisorPage").then((m) => ({
    default: m.SupervisorPage,
  })),
);
const CalendarImportPage = lazy(() =>
  import("@/shared/pages/CalendarImportPage").then((m) => ({
    default: m.CalendarImportPage,
  })),
);
const ForgotPasswordPage = lazy(() =>
  import("@/shared/pages/ForgotPasswordPage").then((m) => ({
    default: m.ForgotPasswordPage,
  })),
);
const AdminPage = lazy(() =>
  import("@/admin/AdminPage").then((m) => ({ default: m.AdminPage })),
);
const InstructorPage = lazy(() =>
  import("@/shared/pages/instructor/InstructorPage").then((m) => ({
    default: m.InstructorPage,
  })),
);
const CourseStudioPage = lazy(() =>
  import("@/shared/pages/CourseStudioPage").then((m) => ({
    default: m.CourseStudioPage,
  })),
);
const JoinPage = lazy(() =>
  import("@/shared/pages/JoinPage").then((m) => ({ default: m.JoinPage })),
);
const CrisisWorkspacePage = lazy(() =>
  import("@/shared/pages/products/CrisisWorkspacePage").then((m) => ({
    default: m.CrisisWorkspacePage,
  })),
);
const TrainingComplianceApp = lazy(() =>
  import("@/products/training-compliance/TrainingComplianceApp").then((m) => ({
    default: m.TrainingComplianceApp,
  })),
);
const CourseCreatorApp = lazy(() =>
  import("@/products/course-creator/CourseCreatorApp").then((m) => ({
    default: m.CourseCreatorApp,
  })),
);
const SharedCoursePage = lazy(() =>
  import("@/shared/pages/SharedCoursePage").then((m) => ({
    default: m.SharedCoursePage,
  })),
);
const SharedPrivateExercisePage = lazy(() =>
  import("@/shared/pages/SharedPrivateExercisePage").then((m) => ({
    default: m.SharedPrivateExercisePage,
  })),
);
const SharedPrivateTranscriptPage = lazy(() =>
  import("@/shared/pages/SharedPrivateTranscriptPage").then((m) => ({
    default: m.SharedPrivateTranscriptPage,
  })),
);
const BranchPage = lazy(() =>
  import("@/shared/pages/solutions/BranchPage").then((m) => ({
    default: m.BranchPage,
  })),
);
const CrisisNegotiationPage = lazy(() =>
  import("@/shared/pages/solutions/CrisisNegotiationPage").then((m) => ({
    default: m.CrisisNegotiationPage,
  })),
);
const RansomwareNegotiationPage = lazy(() =>
  import("@/shared/pages/solutions/RansomwareNegotiationPage").then((m) => ({
    default: m.RansomwareNegotiationPage,
  })),
);
const PoliceDepartmentsPage = lazy(() =>
  import("@/shared/pages/solutions/PoliceDepartmentsPage").then((m) => ({
    default: m.PoliceDepartmentsPage,
  })),
);
const AcademicProgramPage = lazy(() =>
  import("@/shared/pages/solutions/AcademicProgramPage").then((m) => ({
    default: m.AcademicProgramPage,
  })),
);
const ToolCatalogPage = lazy(() =>
  import("@/shared/pages/ToolCatalogPage").then((m) => ({
    default: m.ToolCatalogPage,
  })),
);
const ApiProductPage = lazy(() => import("@/shared/products/api/ApiProductPage").then((m) => ({ default: m.ApiProductPage })));
const ApiDocsPage = lazy(() => import("@/shared/products/api/ApiDocsPage").then((m) => ({ default: m.ApiDocsPage })));
const LtiCallbackPage = lazy(() => import("@/shared/pages/LtiCallbackPage"));
const PrivateExerciseEditorPage = lazy(() =>
  import("@/shared/pages/PrivateExerciseEditorPage").then((m) => ({
    default: m.PrivateExerciseEditorPage,
  })),
);
const PrivateExerciseDebriefPage = lazy(() =>
  import("@/shared/pages/PrivateExerciseDebriefPage").then((m) => ({
    default: m.PrivateExerciseDebriefPage,
  })),
);
const PrivateExerciseRunPage = lazy(() =>
  import("@/shared/pages/PrivateExerciseRunPage").then((m) => ({
    default: m.PrivateExerciseRunPage,
  })),
);
// Global authenticated app shell (gradient + pill menu + icon rail). Replaces
// the dark Sidebar for every in-app vertical. The MHE-only LearnPage/PracticePage
// content pages stay; they now render inside this shared shell.
import { PrivateScenariosPage } from "@/shared/pages/PrivateScenariosPage";
import { CounterpartsPage } from "@/shared/dashboard/workspace/counterparts/CounterpartsPage";
import { CounterpartPage } from "@/shared/dashboard/workspace/counterparts/CounterpartPage";
import { ArchivedSimulationRedirect } from "@/shared/pages/ArchivedSimulationRedirect";
import { ArchivedAnalyzeRedirect } from "@/shared/pages/ArchivedAnalyzeRedirect";
import { archivedAnalyzeTarget } from "@/shared/lib/archivedAnalyzeRoutes";
import { ConnectedSessionPage } from "@/shared/dashboard/workspace/ConnectedSessionPage";
import { isArchivedSimulationPath } from "@/shared/lib/archivedSimulationRoutes";
import { pendingNavigation } from "@/shared/dashboard/workspace/navigationGuards";
import { WorkspaceToolsPage } from "@/shared/dashboard/workspace/WorkspaceToolsPage";
import { WorkspaceProgressPage } from "@/shared/dashboard/workspace/WorkspaceProgressPage";
import { WorkspaceRecordPage } from "@/shared/dashboard/workspace/WorkspaceRecordPage";
import { WorkspaceChat } from "@/shared/dashboard/workspace/WorkspaceChat";
import { CatalogScenarioPage } from "@/shared/dashboard/workspace/catalog/CatalogScenarioPage";
import { RecordingReviewPage } from "@/shared/pages/recording/RecordingReviewPage";
import { RecordingDebriefPage } from "@/shared/pages/recording/RecordingDebriefPage";
import { RecordingsPage } from "@/shared/pages/recording/RecordingsPage";
import {
  RECORDINGS_PATH,
  RECORDING_REVIEW_PATH,
  recordingDebriefId,
} from "@/shared/lib/recordingReviews";
import {
  WorkspacePersonasPage,
  WorkspaceScenariosPage,
} from "@/shared/dashboard/workspace/WorkspacePersonasPage";
import { workspaceRoute } from "@/shared/dashboard/workspace/workspaceRoutes";
import { AppShell } from "@/shared/dashboard/AppShell";
import { PaywallModal } from "@/shared/components/PaywallModal";
import { CookieBanner } from "@/shared/components/CookieBanner";
import { trackPageView } from "@/shared/lib/analytics";
import { ErrorBoundary } from "@/shared/components/ErrorBoundary";
import { ToastProvider } from "@/shared/components/Toast";
import { FeatureGate } from "@/shared/components/FeatureGate";
import { ProductGate } from "@/shared/components/ProductGate";
import type { ProductId } from "@/platform/products";
import type { FeatureFlags } from "@/verticals/types";
import { CRISIS_PROGRAM_COURSE_ID } from "@/shared/lib/cohortCourse";
import { defaultCourseLanding } from "@/shared/lib/defaultCourse";
import { isCourseWeekPath } from "@/shared/lib/courseWeekPath";
import { updateRouteSeo } from "@/shared/lib/pageSeo";
import { EmailVerificationGate } from "@/shared/components/auth/EmailVerificationGate";
import { CourseAccessGate } from "@/shared/components/course/CourseAccessGate";
import "@/index.css";

const PAGE_TITLES: Partial<Record<Route, string>> = {
  "general-demo": "EmpatiQ — Workspace demo",
  "course-wizard-demo": "EmpatiQ — Build your course",
  "workspace-record": "EmpatiQ — Record a conversation",
  "workspace-emery": "EmpatiQ — Emery",
  "workspace-personas": "EmpatiQ — Scenarios",
  "workspace-own-scenarios": "EmpatiQ — My scenarios",
  "catalog-scenario": "EmpatiQ — Scenario",
  "recordings": "EmpatiQ — Reviews",
  "recording-review": "EmpatiQ — New review",
  "recording-debrief": "EmpatiQ — Recording debrief",
  "workspace-tools": "EmpatiQ — Library",
  "workspace-progress": "EmpatiQ — Progress",
  "private-scenarios": "EmpatiQ — Private scenarios",
  home: "Negotiation Training with AI Role-Play | EmpatiQ",
  dashboard: "EmpatiQ — Practice",
  supervisor: "EmpatiQ — AI Supervisor",
  sessions: "EmpatiQ — Conversations",
  counterparts: "EmpatiQ — Counterparts",
  "counterpart-detail": "EmpatiQ — Counterpart",
  "conversation-maps": "EmpatiQ — Conversation Map",
  profile: "EmpatiQ — Settings",
  courses: "EmpatiQ — Courses",
  lesson: "EmpatiQ — Lesson",
  "client-session": "EmpatiQ — Session",
  cases: "EmpatiQ — Case Files",
  "case-detail": "EmpatiQ — Case File",
  "case-session": "EmpatiQ — After-Action Review",
  admin: "EmpatiQ — Admin",
  instructor: "EmpatiQ — Instructor",
  studio: "EmpatiQ — Course Studio",
  tools: "EmpatiQ — Products",
  "product-api": "EmpatiQ API — Emotion Analysis, Emery & Digital Twins",
  "api-docs": "EmpatiQ API Documentation",
  universities: "EmpatiQ for Universities",
  "academic-program": "EmpatiQ for Higher Education",
  "product-crisis":
    "AI Crisis Negotiation & Incident Review — Crisis Workspace | EmpatiQ",
  "product-demo": "EmpatiQ — Demo Workspace",
  "compliance-app": "EmpatiQ — Readiness",
  "creator-app": "EmpatiQ — Course Creator",
  "shared-course": "EmpatiQ — Shared Course",
  "shared-private-exercise": "EmpatiQ — Shared Scenario",
  "shared-private-transcript": "EmpatiQ — Shared Transcript",
  "private-exercise-edit": "EmpatiQ — Private Exercise",
  "private-exercise-run": "EmpatiQ — Private Exercise",
  "private-exercise-debrief": "EmpatiQ — Private Debrief",
};

type Route =
  | "general-demo"
  | "course-wizard-demo"
  | "workspace-record"
  | "workspace-emery"
  | "workspace-personas"
  | "workspace-own-scenarios"
  | "catalog-scenario"
  | "recordings"
  | "recording-review"
  | "recording-debrief"
  | "archived-simulation"
  | "archived-analyze"
  | "private-scenarios"
  | "workspace-tools"
  | "workspace-progress"
  | "home"
  | "dashboard"
  | "sessions"
  | "counterparts"
  | "counterpart-detail"
  | "client-session"
  | "cases"
  | "case-detail"
  | "case-session"
  | "login"
  | "signup"
  | "forgot-password"
  | "reset-password"
  | "conversation-maps"
  | "profile"
  | "course"
  | "expert"
  | "lesson"
  | "courses"
  | "program"
  | "certificate"
  | "terms"
  | "privacy"
  | "refunds"
  | "contact"
  | "supervisor"
  | "calendar-import"
  | "admin"
  | "instructor"
  | "studio"
  | "join"
  | "lti-callback"
  | "tools"
  | "product-api"
  | "api-docs"
  | "universities"
  | "solution"
  | "crisis-negotiation"
  | "ransomware-negotiation"
  | "police-departments"
  | "academic-program"
  | "product-crisis"
  | "product-demo"
  | "compliance-app"
  | "walkie-test"
  | "creator-app"
  | "shared-course"
  | "shared-private-exercise"
  | "shared-private-transcript"
  | "live-session"
  | "private-exercise-edit"
  | "private-exercise-debrief"
  | "private-exercise-run";

function getRouteFromPath(rawPathInput: string): Route {
  // Drop query string and hash so paths like "/contact?industry=foo#bar"
  // still match the "/contact" route handlers below.
  const rawPath = rawPathInput.split("?")[0].split("#")[0];
  if (/^\/products\/api\/?$/.test(rawPath)) return "product-api";
  if (/^\/docs\/api\/?$/.test(rawPath)) return "api-docs";
  if (isGeneralDemoPath(rawPath)) return "general-demo";
  if (/^\/course-demo\/?$/.test(rawPath)) return "course-wizard-demo";
  if (
    typeof window !== "undefined" &&
    productIntentFromHost(window.location.hostname) === "crisis"
  ) {
    return "product-crisis";
  }
  // Product subdomains: the bare root is that product's login
  // front door, not the marketing home. The login/signup pages self-theme off
  // the same host, so no explicit prefix is needed there.
  if (typeof window !== "undefined" && (rawPath === "/" || rawPath === "")) {
    const hostProduct = productIntentFromHost(window.location.hostname);
    if (hostProduct)
      return hostProduct === "crisis" ? "product-crisis" : "login";
  }
  // Bare product roots get dedicated landing pages instead of falling through
  // to the prefix-stripped "home" route.
  if (
    /^\/solutions\/universities\/(psychology-programs|law-schools|criminal-justice-programs)\/?$/.test(
      rawPath,
    )
  )
    return "academic-program";
  if (
    rawPath === "/solutions/universities" ||
    rawPath === "/solutions/universities/"
  )
    return "universities";
  if (/^\/solutions\/mental-health\/?$/.test(rawPath)) return "solution";
  if (/^\/solutions\/crisis-negotiation\/?$/.test(rawPath))
    return "crisis-negotiation";
  if (/^\/solutions\/ransomware-negotiation\/?$/.test(rawPath))
    return "ransomware-negotiation";
  if (/^\/solutions\/police-departments\/?$/.test(rawPath))
    return "police-departments";
  if (archivedAnalyzeTarget(rawPath)) return "archived-analyze";
  if (/^\/compliance\/demo(?:\/.*)?$/.test(rawPath)) return "product-demo";
  if (/^\/(crisis|npp)(?:\/.*)?$/.test(rawPath)) return "product-crisis";
  if (
    /^\/compliance\/(dashboard|officers(?:\/[^/]+)?|assignments(?:\/[^/]+(?:\/run)?)?|course|training|certifications|vault|reports|settings)\/?$/.test(
      rawPath,
    ) ||
    rawPath === "/compliance" ||
    rawPath === "/compliance/"
  )
    return "compliance-app";
  if (/^\/creator(\/.*)?$/.test(rawPath)) return "creator-app";
  if (/^\/shared\/course\/[^/]+\/?$/.test(rawPath)) return "shared-course";
  if (/^\/shared\/exercise\/[^/]+\/?$/.test(rawPath))
    return "shared-private-exercise";
  if (/^\/shared\/transcript\/[^/]+\/?$/.test(rawPath))
    return "shared-private-transcript";
  const path = rawPath;
  if (isArchivedSimulationPath(path)) return "archived-simulation";
  if (path === "/exercises" || path === "/exercises/")
    return "private-scenarios";
  const workspacePage = workspaceRoute(path);
  if (workspacePage) return workspacePage;
  if (/^\/scenarios\/[^/]+\/?$/.test(path)) return "catalog-scenario";
  if (path === RECORDINGS_PATH || path === `${RECORDINGS_PATH}/`)
    return "recordings";
  if (path === RECORDING_REVIEW_PATH || path === `${RECORDING_REVIEW_PATH}/`)
    return "recording-review";
  if (recordingDebriefId(path)) return "recording-debrief";
  if (path === "/exercises/new" || path === "/exercises/new/")
    return "private-exercise-edit";
  if (/^\/exercises\/[^/]+\/edit\/?$/.test(path))
    return "private-exercise-edit";
  if (/^\/exercises\/[^/]+\/debrief\/[^/]+\/?$/.test(path))
    return "private-exercise-debrief";
  if (/^\/exercises\/[^/]+\/run\/?$/.test(path)) return "private-exercise-run";
  if (path === "/sessions" || path === "/sessions/") return "sessions";
  if (path === "/counterparts" || path === "/counterparts/")
    return "counterparts";
  if (/^\/counterparts\/[^/]+\/?$/.test(path)) return "counterpart-detail";
  if (/^\/clients\/[^/]+\/sessions\/[^/]+/.test(path)) return "client-session";
  if (path === "/cases" || path === "/cases/") return "cases";
  if (/^\/cases\/[^/]+\/sessions\/[^/]+/.test(path)) return "case-session";
  if (/^\/cases\/[^/]+/.test(path)) return "case-detail";
  if (path === "/courses" || path === "/courses/") return "courses";
  if (path === "/program" || path === "/program/") return "program";
  if (path === "/conversation-maps" || path.startsWith("/conversation-maps/"))
    return "conversation-maps";
  if (path === "/profile" || path === "/profile/") return "profile";
  if (path === "/progress" || path === "/progress/") return "profile";
  // Per-workspace auth front doors for product paths and subdomains. They
  // render the shared LoginPage/SignupPage, which resolve the product intent
  // from the path (or host) to theme the rail and set the signup default vertical.
  if (
    [
      "/crisis/login",
      "/crisis/login/",
      "/npp/login",
      "/npp/login/",
    ].includes(path)
  )
    return "login";
  if (
    [
      "/crisis/signup",
      "/crisis/signup/",
      "/npp/signup",
      "/npp/signup/",
    ].includes(path)
  )
    return "signup";
  if (path === "/login" || path === "/login/") return "login";
  if (path === "/signup" || path === "/signup/") return "signup";
  if (path === "/forgot-password" || path === "/forgot-password/")
    return "forgot-password";
  if (path === "/reset-password" || path === "/reset-password/")
    return "reset-password";
  if (/^\/course\/[^/]+\/lesson\/\d+/.test(path)) return "lesson";
  if (/^\/course\/[^/]+\/live\/[^/]+/.test(path)) return "live-session";
  if (path.startsWith("/course/")) return "course";
  if (path.startsWith("/expert/")) return "expert";
  if (path.startsWith("/certificate/")) return "certificate";
  if (path === "/terms" || path === "/terms/") return "terms";
  if (path === "/privacy" || path === "/privacy/") return "privacy";
  if (path === "/refunds" || path === "/refunds/") return "refunds";
  if (path === "/contact" || path === "/contact/") return "contact";
  if (path === "/tools" || path === "/tools/") return "tools";
  if (path === "/transplant-dashboard" || path === "/transplant-dashboard/")
    return "dashboard";
  if (path === "/supervisor" || path === "/supervisor/") return "supervisor";
  if (path === "/calendar/import" || path === "/calendar/import/")
    return "calendar-import";
  if (path === "/admin" || path === "/admin/") return "admin";
  if (path === "/instructor" || path === "/instructor/") return "instructor";
  if (path === "/studio" || path === "/studio/") return "studio";
  if (path.startsWith("/join/")) return "join";
  if (path === "/lti/callback" || path === "/lti/callback/")
    return "lti-callback";
  if (path === "/walkie-test" || path === "/walkie-test/") return "walkie-test";
  return "home";
}

/** In-app routes: which feature gates them, and which are browsable signed out.
 *  Everything here renders inside AppRouteChrome. */
const IN_APP_ROUTES: Partial<
  Record<
    Route,
    {
      feature?: keyof FeatureFlags;
      publicBrowse?: boolean;
      product?: ProductId;
    }
  >
> = {
  "workspace-record": {},
  "archived-analyze": {},
  "workspace-emery": {},
  "workspace-personas": {},
  "workspace-own-scenarios": {},
  "catalog-scenario": {},
  recordings: {},
  "recording-review": {},
  "recording-debrief": {},
  "workspace-tools": {},
  "workspace-progress": {},
  "private-scenarios": {},
  "archived-simulation": {},
  dashboard: {},
  sessions: {},
  counterparts: {},
  "counterpart-detail": {},
  "private-exercise-edit": {},
  "private-exercise-debrief": {},
  "private-exercise-run": {},
  "conversation-maps": {},
  profile: {},
  supervisor: {},
  "client-session": { feature: "clientSessions" },
  cases: { product: "crisis-ops" },
  "case-detail": { product: "crisis-ops" },
  "case-session": { product: "crisis-ops" },
  "calendar-import": { feature: "calendarImport" },
  "compliance-app": { product: "compliance" },
  course: { feature: "courses" },
  expert: { feature: "courses" },
  program: { feature: "courses" },
  // The catalog is account-only: nothing public links to it, and a visitor who
  // reaches /courses signs in first, returning here afterwards.
  courses: { feature: "courses" },
};

/**
 * The one chrome every in-app route renders through. Its shape is fixed —
 * guard, verification, feature gate, shell — so navigating between routes
 * updates the page in place instead of tearing the shell down and rebuilding
 * it. Routes used to nest these four in five different arrangements, which is
 * what made every navigation flash: a different tree shape at the same position
 * remounts everything below it.
 */
function AppRouteChrome({
  pathname,
  children,
  feature,
  product,
  requireAuth = true,
}: {
  pathname: string;
  children: ReactNode;
  feature?: keyof FeatureFlags;
  product?: ProductId;
  requireAuth?: boolean;
}) {
  return (
    <ProtectedRoute enabled={requireAuth}>
      <EmailVerificationGate>
        <FeatureGate feature={feature}>
          <AppRoute pathname={pathname}>
            <ProductGate product={product}>{children}</ProductGate>
          </AppRoute>
        </FeatureGate>
      </EmailVerificationGate>
    </ProtectedRoute>
  );
}

function ProtectedRoute({
  children,
  enabled = true,
}: {
  children: ReactNode;
  enabled?: boolean;
}) {
  const { user, loading: authLoading } = useAuth();
  const {
    loading: subLoading,
    showPaywall,
    setShowPaywall,
  } = useSubscription();
  const { loading: verticalLoading } = useVertical();

  const waiting = enabled && (authLoading || subLoading || verticalLoading);
  const signedOut = enabled && !waiting && !user;

  if (signedOut) {
    // Remember where they were headed so sign-in can return them there.
    const current = window.location.pathname + window.location.search;
    if (current && current !== "/login" && current !== "/login/") {
      try {
        sessionStorage.setItem("loginRedirectTo", current);
      } catch {
        /* ignore */
      }
    }
    const loginPath =
      current.startsWith("/crisis") || current.startsWith("/npp")
        ? "/crisis/login"
        : "/login";
    window.history.pushState({}, "", loginPath);
  }

  // ONE element in every state. Returning a fragment while public and a <div>
  // while guarded changed the element type at this position, and React tore
  // down everything below — the shell included — on every move between a public
  // route and a guarded one.
  return (
    <div>
      {waiting ? (
        <div className="flex min-h-screen items-center justify-center bg-[var(--page-bg)]">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-[var(--teal-700)] border-t-transparent" />
        </div>
      ) : signedOut ? (
        <LoginPage />
      ) : (
        children
      )}
      {user && (
        <PaywallModal
          isOpen={showPaywall}
          onClose={() => setShowPaywall(false)}
        />
      )}
    </div>
  );
}

// Wraps an authenticated in-app page in the global AppShell (gradient canvas +
// top pill menu + thin icon rail) for EVERY vertical. The shell owns all chrome,
// so wrapped pages render as pure content. `onlyMhe` marks MHE-only greenfield
// routes (/learn, /practice, /progress) that have no equivalent in other
// verticals — those verticals are redirected to /dashboard.
function AppRoute({
  pathname,
  children,
}: {
  pathname: string;
  children: ReactNode;
}) {
  return <AppShell pathname={pathname}>{children}</AppShell>;
}

/** The app entry stays neutral for ordinary accounts. Organization members with
 * an assigned course enter that course directly, just as a saved Codex workspace
 * opens into its actual work instead of a second branded deployment. */
function DashboardEntry() {
  const { resolved, vertical, defaultCourseId } = useVertical();
  const [skipAssignedCourse] = useState(() => {
    try {
      const skip =
        sessionStorage.getItem("empatiq:skip-assigned-course-once") === "1";
      if (skip) sessionStorage.removeItem("empatiq:skip-assigned-course-once");
      return skip;
    } catch {
      return false;
    }
  });
  // A landing-page CTA's destination, for sign-ins that Supabase returns here
  // (Google, the email-confirmation link).
  const [pending] = useState(takeDestination);
  // An assigned course is the workspace's decision, not a browser preference,
  // so it wins over the pending destination. Accounts with neither stay on
  // the neutral dashboard.
  const target =
    (resolved && vertical === "general" && !skipAssignedCourse
      ? defaultCourseLanding(
          `${window.location.pathname}${window.location.search}`,
          defaultCourseId,
        )
      : null) ?? (resolved ? pending : null);

  useEffect(() => {
    if (!target) return;
    // Defer until Router has installed its popstate listener, then replace the
    // neutral entry so Back does not bounce through the redirect again.
    const timer = window.setTimeout(() => {
      window.history.replaceState({}, "", target);
      window.dispatchEvent(new PopStateEvent("popstate"));
    }, 0);
    return () => window.clearTimeout(timer);
  }, [target]);

  if (target) {
    return (
      <div
        className="flex min-h-[50vh] items-center justify-center bg-white"
        aria-label="Opening assigned course"
      >
        <div className="h-7 w-7 animate-spin rounded-full border-2 border-[#0c1a33] border-t-transparent" />
      </div>
    );
  }
  return <DashboardPage />;
}

function PublicOnlyRoute({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth();
  const { loading: verticalLoading, vertical, defaultCourseId } = useVertical();
  const destination =
    (vertical === "general"
      ? defaultCourseLanding("/dashboard", defaultCourseId)
      : null) ?? "/dashboard";

  useEffect(() => {
    if (!user || loading || verticalLoading) return;
    // Let the router mount the authenticated shell. Rendering DashboardPage
    // here exposed the generic app launcher while the FAU assignment loaded.
    const timer = window.setTimeout(() => {
      window.history.replaceState({}, "", destination);
      window.dispatchEvent(new PopStateEvent("popstate"));
    }, 0);
    return () => window.clearTimeout(timer);
  }, [user, loading, verticalLoading, destination]);

  if (loading || user) {
    return (
      <div
        className="min-h-screen bg-[var(--page-bg)] flex items-center justify-center"
        role="status"
        aria-label="Opening your workspace"
      >
        <div className="w-8 h-8 border-2 border-[var(--teal-700)] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return children;
}

/** Stable geometry while a lazy route chunk arrives. A centered spinner made
 * the content column look empty and then jump into a completely different
 * shape; these quiet paper blocks preserve the page's reading rhythm. */
function RouteContentSkeleton() {
  return (
    <div
      role="status"
      aria-label="Loading page"
      className="mx-auto w-full max-w-5xl px-6 py-10 sm:px-10 sm:py-12"
    >
      <span className="sr-only">Loading page…</span>
      <div aria-hidden="true" className="space-y-8">
        <div className="space-y-3">
          <div className="h-2.5 w-24 rounded-[1px] bg-[var(--paper-track)]" />
          <div className="h-8 w-3/5 max-w-xl rounded-[2px] bg-[var(--paper-track)]" />
          <div className="h-3 w-4/5 max-w-2xl rounded-[1px] bg-[var(--paper-sunken)]" />
        </div>
        <div className="grid gap-3 md:grid-cols-2">
          {[0, 1, 2, 3].map((item) => (
            <div
              key={item}
              className="min-h-28 border border-[var(--line-soft)] bg-[var(--paper-raised)] p-5"
            >
              <div className="h-3 w-1/2 rounded-[1px] bg-[var(--paper-track)]" />
              <div className="mt-4 h-2.5 w-full rounded-[1px] bg-[var(--paper-sunken)]" />
              <div className="mt-2 h-2.5 w-3/4 rounded-[1px] bg-[var(--paper-sunken)]" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// Route changes cross-fade via the View Transitions API where the browser has
// it; elsewhere the .animate-page-in entrance in globals.css remains the whole
// effect — the two must not stack, so the page div drops the class here.
const supportsViewTransitions = "startViewTransition" in document;

type ViewTransitionDocument = Document & {
  startViewTransition?: (update: () => void | Promise<void>) => {
    ready: Promise<void>;
  };
};

function Router() {
  const [route, setRoute] = useState<Route>(() => {
    return getRouteFromPath(window.location.pathname);
  });
  const [pathname, setPathname] = useState<string>(
    () => window.location.pathname,
  );
  const [, setLocationRevision] = useState(0);
  const scrollAfterRouteCommit = useRef(false);
  const lastCommittedPathname = useRef(window.location.pathname);
  const lastCommittedUrl = useRef(
    window.location.pathname + window.location.search + window.location.hash,
  );
  const navigationAttempt = useRef(0);
  const routeTransitionDone = useRef<(() => void) | null>(null);

  // Route-state commit shared by navigate() and popstate. With view-transition
  // support (and motion welcome), the browser snapshots the outgoing page and
  // cross-fades it into the incoming one. The update promise resolves only
  // after React commits the destination — see the [pathname] effect below — so
  // a lazy route chunk finishes loading behind the held old page and the fade
  // lands on real content, never the Suspense skeleton.
  const commitRoute = (nextPathname: string) => {
    const update = () => {
      // A transition update keeps the current page visible when the destination
      // route chunk is still loading instead of flashing the Suspense fallback.
      startTransition(() => {
        setRoute(getRouteFromPath(nextPathname));
        setPathname(nextPathname);
        // Query-only navigation (e.g. Record Session from Client Roster) must
        // update the shell and page even when the pathname is unchanged.
        setLocationRevision((revision) => revision + 1);
      });
    };
    const changesPage = nextPathname !== lastCommittedPathname.current;
    lastCommittedPathname.current = nextPathname;
    lastCommittedUrl.current =
      window.location.pathname + window.location.search + window.location.hash;
    const startViewTransition = (document as ViewTransitionDocument)
      .startViewTransition;
    if (
      !changesPage ||
      !startViewTransition ||
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    ) {
      update();
      return;
    }
    const transition = startViewTransition.call(
      document,
      () =>
        new Promise<void>((resolve) => {
          // A navigation landing mid-transition makes the browser skip the
          // previous animation; release its resolver so nothing waits on it.
          routeTransitionDone.current?.();
          routeTransitionDone.current = resolve;
          update();
        }),
    );
    // A skipped transition (hidden tab, or a newer navigation arriving before
    // this one finishes animating) rejects `ready` with InvalidStateError even
    // though the navigation itself committed fine — expected, not an error.
    transition.ready.catch(() => {});
  };

  useEffect(() => {
    const handlePopState = async () => {
      const attempt = ++navigationAttempt.current;
      const pending = pendingNavigation();
      if (pending && !(await pending)) {
        if (attempt === navigationAttempt.current)
          window.history.replaceState({}, "", lastCommittedUrl.current);
        return;
      }
      if (attempt === navigationAttempt.current)
        commitRoute(window.location.pathname);
    };
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  useEffect(() => {
    (window as any).navigate = async (path: string) => {
      if (isGeneralDemo()) path = generalDemoDestination(path);
      if (isGeneralDemoPath(path) && !path.includes("?") && new URLSearchParams(window.location.search).get("preview") === "1") path += "?preview=1";
      const attempt = ++navigationAttempt.current;
      const pending = pendingNavigation();
      if (pending && !(await pending)) return;
      if (attempt !== navigationAttempt.current) return;
      const nextUrl = new URL(path, window.location.href);
      const changesPage = nextUrl.pathname !== window.location.pathname;
      window.history.pushState(
        {},
        "",
        `${nextUrl.pathname}${nextUrl.search}${nextUrl.hash}`,
      );
      scrollAfterRouteCommit.current = changesPage;

      // Keep router state as a pathname. Query parameters belong in
      // window.location.search; including them here breaks ID extraction for
      // routes such as /course/:id?primary=1.
      commitRoute(nextUrl.pathname);
    };
  }, []);

  useEffect(() => {
    if (!scrollAfterRouteCommit.current) return;
    scrollAfterRouteCommit.current = false;
    // Scroll only after the destination has committed. Doing this before a lazy
    // chunk resolves makes the still-visible previous page jump to its top.
    window.scrollTo(0, 0);
  }, [pathname]);

  useEffect(() => {
    // The destination page has committed (and the scroll effect above has
    // already reset the viewport) — let the view transition capture the new
    // state and start animating.
    routeTransitionDone.current?.();
    routeTransitionDone.current = null;
  }, [pathname]);

  useEffect(() => {
    updateRouteSeo(
      pathname,
      pathname === "/progress"
        ? "EmpatiQ — Progress"
        : (PAGE_TITLES[route] ?? "EmpatiQ"),
    );
    trackPageView();
  }, [pathname, route]);

  const inApp = IN_APP_ROUTES[route];

  const routeContent = (() => {
    switch (route) {
      case "general-demo":
        return <GeneralWorkspaceDemoPage pathname={pathname} />;
      case "course-wizard-demo":
        return <CourseWizardDemoPage />;
      case "archived-simulation":
        return <ArchivedSimulationRedirect />;
      case "private-scenarios":
        return <PrivateScenariosPage />;
      case "workspace-tools":
        return <WorkspaceToolsPage />;
      case "workspace-progress":
        return <WorkspaceProgressPage />;
      case "workspace-record":
        return <WorkspaceRecordPage />;
      case "workspace-emery":
        return <WorkspaceChat />;
      case "workspace-personas":
        return <WorkspaceScenariosPage />;
      case "workspace-own-scenarios":
        return <WorkspacePersonasPage />;
      case "catalog-scenario":
        return <CatalogScenarioPage key={pathname} />;
      case "recordings":
        return <RecordingsPage />;
      case "recording-review":
        return <RecordingReviewPage />;
      case "recording-debrief":
        return <RecordingDebriefPage key={pathname} />;

      case "dashboard":
        return <DashboardEntry />;

      case "sessions":
        return <SessionsPage />;

      case "counterparts":
        return <CounterpartsPage />;

      case "counterpart-detail":
        return <CounterpartPage key={pathname} />;

      case "private-exercise-edit":
        return <PrivateExerciseEditorPage />;

      case "private-exercise-debrief":
        return <PrivateExerciseDebriefPage />;

      case "private-exercise-run":
        return <PrivateExerciseRunPage />;

      case "archived-analyze":
        return <ArchivedAnalyzeRedirect />;

      case "client-session":
        return <ConnectedSessionPage />;

      case "cases":
        return <CrisisCasesPage />;

      case "case-detail":
        return <CrisisCaseDetailPage />;

      case "case-session":
        return <CrisisRecordedSessionPage />;

      case "conversation-maps":
        return <ConversationMapPage />;

      case "profile":
        return <ProfilePage />;

      case "lesson":
        return (
          <ProtectedRoute>
            <FeatureGate feature="courses">
              <EmailVerificationGate>
                <CourseAccessGate pathname={pathname}>
                  <LessonPage key={window.location.pathname} />
                </CourseAccessGate>
              </EmailVerificationGate>
            </FeatureGate>
          </ProtectedRoute>
        );
      case "live-session":
        // Full-screen meeting room (no AppRoute shell) — like "lesson".
        return (
          <ProtectedRoute>
            <FeatureGate feature="courses">
              <EmailVerificationGate>
                <CourseAccessGate pathname={pathname}>
                  <CourseLiveSessionPage key={window.location.pathname} />
                </CourseAccessGate>
              </EmailVerificationGate>
            </FeatureGate>
          </ProtectedRoute>
        );
      // A course renders its syllabus; a week inside it renders the step.
      // Both are generic — the id only picks which course.
      case "course": {
        const courseId = decodeURIComponent(
          pathname.match(/^\/course\/([^/]+)/)?.[1] ?? "",
        );
        const content = isCourseWeekPath(pathname) ? (
          <CourseWeekPage />
        ) : courseId === CRISIS_PROGRAM_COURSE_ID ? (
          <CourseProgramPage />
        ) : (
          <CoursePage />
        );
        return (
          <CourseAccessGate pathname={pathname}>{content}</CourseAccessGate>
        );
      }

      case "expert":
        return <ExpertPage />;

      case "courses":
        // Public: the catalog itself needs no account (browsable, locked/coming
        // categories shown honestly); opening a specific course gates on auth
        // inside CoursesPage (navRequiringAuth), same return-after-login flow
        // ProtectedRoute uses elsewhere.
        return <CoursesPage />;

      case "program":
        return <ProgramPage />;

      case "certificate":
        return (
          <EmailVerificationGate>
            <CertificateViewPage />
          </EmailVerificationGate>
        );
      case "terms":
        return <TermsPage />;
      case "privacy":
        return <PrivacyPage />;
      case "refunds":
        return <RefundPolicyPage />;
      case "contact":
        return <ContactPage />;
      case "tools":
        return <ToolCatalogPage />;
      case "product-api":
        return <ApiProductPage />;
      case "api-docs":
        return <ApiDocsPage />;
      case "supervisor":
        return <SupervisorPage />;

      case "calendar-import":
        return <CalendarImportPage />;

      case "admin":
        return (
          <ProtectedRoute>
            <AdminPage />
          </ProtectedRoute>
        );
      case "instructor":
        return (
          <ProtectedRoute>
            <InstructorPage />
          </ProtectedRoute>
        );
      case "studio":
        return (
          <ProtectedRoute>
            <CourseStudioPage />
          </ProtectedRoute>
        );
      case "login":
        return (
          <PublicOnlyRoute>
            <LoginPage />
          </PublicOnlyRoute>
        );
      case "signup":
        return (
          <PublicOnlyRoute>
            <SignupPage />
          </PublicOnlyRoute>
        );
      case "forgot-password":
        return <ForgotPasswordPage />;
      case "reset-password":
        // NOT PublicOnlyRoute: a recovery-link arrival is already signed in and
        // must see the set-password form, not get bounced to /dashboard.
        return <ResetPasswordPage />;
      case "join":
        return <JoinPage />;
      case "lti-callback":
        return <LtiCallbackPage />;
      case "walkie-test":
        return (
          <Suspense
            fallback={
              <div className="min-h-screen flex items-center justify-center text-sm text-neutral-500">
                Loading tuner…
              </div>
            }
          >
            <WalkieTestPage />
          </Suspense>
        );
      case "universities":
        return <BranchPage slug="universities" />;
      case "solution":
        return <BranchPage slug="mental-health" />;
      case "crisis-negotiation":
        return <CrisisNegotiationPage />;
      case "ransomware-negotiation":
        return <RansomwareNegotiationPage />;
      case "police-departments":
        return <PoliceDepartmentsPage />;
      case "academic-program": {
        const slug = pathname.match(
          /^\/solutions\/universities\/([^/]+)\/?$/,
        )?.[1] as AcademicProgramSlug | undefined;
        return <AcademicProgramPage slug={slug ?? "psychology-programs"} />;
      }
      case "product-crisis":
        return <CrisisWorkspacePage />;
      case "product-demo":
        return <TrainingComplianceApp pathname={pathname} guestDemo />;
      case "compliance-app":
        return (
          <TrainingComplianceApp
            pathname={
              pathname === "/compliance" || pathname === "/compliance/"
                ? "/compliance/dashboard"
                : pathname
            }
            unified
          />
        );
      case "creator-app":
        return (
          <ProtectedRoute>
            <EmailVerificationGate>
              <ProductGate product="creator">
                <CourseCreatorApp pathname={pathname} />
              </ProductGate>
            </EmailVerificationGate>
          </ProtectedRoute>
        );
      case "shared-course": {
        const shareToken =
          pathname.match(/^\/shared\/course\/([^/]+)/)?.[1] ?? "";
        return (
          <EmailVerificationGate>
            <SharedCoursePage token={decodeURIComponent(shareToken)} />
          </EmailVerificationGate>
        );
      }
      case "shared-private-exercise": {
        const shareToken =
          pathname.match(/^\/shared\/exercise\/([^/]+)/)?.[1] ?? "";
        return (
          <SharedPrivateExercisePage token={decodeURIComponent(shareToken)} />
        );
      }
      case "shared-private-transcript": {
        const shareToken =
          pathname.match(/^\/shared\/transcript\/([^/]+)/)?.[1] ?? "";
        return (
          <SharedPrivateTranscriptPage token={decodeURIComponent(shareToken)} />
        );
      }
      default:
        return <HomePage />;
    }
  })();

  // The key lives on the PAGE, not on the whole tree. Keying the outer div
  // remounted the shell on every route change — the chrome above exists to
  // prevent exactly that — and the entrance animation made it visible as a
  // flash. Now the page fades in and the shell stays put. The CSS entrance
  // only applies where view transitions can't run: stacking both double-fades
  // the incoming page.
  const pageKey = route;
  const pageMotion = supportsViewTransitions ? "" : "animate-page-in";
  const page = (
    <div
      key={pageKey}
      className={
        inApp
          ? `eq-route-page flex min-h-0 flex-1 flex-col ${pageMotion}`
          : pageMotion
      }
    >
      {routeContent}
    </div>
  );

  // The Suspense boundary sits INSIDE the chrome, not around it. Pages are
  // lazy, so a boundary above the shell replaced the sidebar with a spinner
  // every time a route's chunk hadn't loaded yet — the shell was rebuilt on
  // arrival, and that was the flash. Now only the content area waits.
  const fallback = <RouteContentSkeleton />;

  if (inApp) {
    return (
      <AppRouteChrome
        pathname={pathname}
        feature={inApp.feature}
        product={inApp.product}
        requireAuth={!inApp.publicBrowse}
      >
        <Suspense fallback={fallback}>{page}</Suspense>
      </AppRouteChrome>
    );
  }

  return (
    <Suspense
      fallback={
        <div className="flex min-h-screen items-center justify-center bg-[var(--page-bg)]">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-[var(--teal-700)] border-t-transparent" />
        </div>
      }
    >
      {page}
    </Suspense>
  );
}

export function App() {
  return (
    <ErrorBoundary>
      <ToastProvider>
        <LanguageProvider>
          <AuthProvider>
            <ProductProvider>
              <VerticalProvider>
                <SubscriptionProvider>
                  <Router />
                  <CookieBanner />
                </SubscriptionProvider>
              </VerticalProvider>
            </ProductProvider>
          </AuthProvider>
        </LanguageProvider>
      </ToastProvider>
    </ErrorBoundary>
  );
}

export default App;
